import { useEffect, useState } from "react";
import { useMainContext } from "../../contexts/main";
import { useProfileQuery } from "../../services/react-query/useProfileQuery";
import {
    CFlexCenter,
    DepositIcon,
    DText,
    FlexBetween,
    FlexCenter,
    Margin,
    Padding,
    Tooltip
} from "../../styles/CommonStyles";
import { TradeBtn } from "../../styles/TradeStyles";
import { formatNumber, stringToNumber } from "../../utils/common";
import Text from "../../utils/Text";
import Slider from "./Slider";
import TradeInput from "./TradeInput";
import {useWindowSize} from "../../hooks/common/useWindowSize";
import {MOBILE_SIZE, TABLET_SIZE} from "../../constants/common";
import {useAvailableCoin} from "../../services/react-query/useMarketQuery";
import {Flex} from "../../styles/MarketStyles";
import {TextWithMargin} from "../../styles/OrdersStyle";
import {HOME} from "../../constants/urls";
import {useAuthContext} from "../../contexts/auth";


const TradeType = ({
    type,
    activeTab,
    userInput,
    setUserInput,
    onOperationClicked,
    valid,
    ocoError
}) => {

    const { profile } = useAuthContext()
    const {data: availableCoins} = useAvailableCoin()
    const { market, main: { lang } } = useMainContext()
    const { width } = useWindowSize()

    const [sliderVal, setSliderVal] = useState(0)
    const [available, setAvailable] = useState({
        buy: 0, sell: 0
    })

    const [limits, setLimits] = useState({
        max: "", min: ""
    })

    const [depositHover, setDepositHover] = useState(false)

    const limitErrorInitial = {
        error: false, type: "", value: "", userError: false
    }
    const [limitsError, setLimitsError] = useState(limitErrorInitial)

    /**
     * setting available balances
     */
    useEffect(() => {
        const sell = profile?.coins?.find(c => c.coin === market.coin)
        const buy = profile?.coins?.find(c => c.coin === market.pair)
        if (market.pair === "irt") {
            setAvailable({ buy: profile?.balance ? profile?.balance : 0, sell: sell ? sell.amount : 0 })
        } else {
            setAvailable({ buy: buy ? buy.amount : 0, sell: sell ? sell.amount : 0 })
        }
    }, [profile, market])


    /**
     * setting order limits
     */
    useEffect(() => {
        if (availableCoins) {
            const coin = availableCoins.find(c => c.id === market.coin)
            if (coin) {
                setLimits({
                    max: coin.order.max, min: coin.order.min
                })
            }
        }
    }, [availableCoins, market])


    /**
     * setting limit errors
     */
    useEffect(() => {
        const amount = stringToNumber(userInput.amount)

        let userBalance = available[type]
        if (type === "buy") {
            userBalance = available[type] / stringToNumber(userInput.price)
        }

        if (amount > limits.max || amount > parseFloat(parseFloat(userBalance).toFixed(6))) {
            setLimitsError({error: true, type: "max", value: limits.max, userError: amount > userBalance})
        }else if (amount < limits.min || !amount) {
            setLimitsError({error: true, type: "min", value: limits.min, userError: false})
        }else{
            setLimitsError(limitErrorInitial)
        }
    }, [userInput, limits, available])


    // input change
    const onValueChange = (val, type) => {
        let total;
        let amount;
        switch (type) {
            case "price":
                total = stringToNumber(val) * stringToNumber(userInput.amount)
                setUserInput(state => ({ ...state, price: val, total: formatNumber(total, { type: 'irt' }) }))
                break
            case "amount":
                total = stringToNumber(userInput.price) * stringToNumber(val)
                if (activeTab === 2) total = stringToNumber(val) * stringToNumber(userInput.limit)
                setUserInput(state => ({ ...state, total: formatNumber(total, {type: 'irt'}), amount: val }))
                break
            case "total":
                amount = stringToNumber(val) / stringToNumber(userInput.price)
                if (activeTab === 2) amount = stringToNumber(val) / stringToNumber(userInput.limit)
                setUserInput(state => ({ ...state, amount: formatNumber(amount, {point: 6}), total: val }))
                break
            case "limit":
                setUserInput(state => {
                  const newState = {...state}
                  newState.limit = val
                  if (activeTab === 2) {
                      newState.price = val
                  }
                  return newState
                })
                break
            case "stop":
                setUserInput(state => ({...state, stop: val}))
                break
            default:
                break
        }
    }


    // slider change
    const onSliderChange = (val) => {
        setSliderVal(val)

        const getPrice = (price) => {
            return (price !== 0 ? stringToNumber(price) > 1 ? stringToNumber(price) : price : 0)
        }
        if (!getPrice(userInput.price)) {
            return
        }

        let amount;
        let total;
        if (type === "buy") {
            amount = ((available[type] * val) / 100) / getPrice(userInput.price)
            total = amount * getPrice(userInput.price)
        } else {
            amount = (available[type] * val) / 100
            total = amount * getPrice(userInput.price)
        }
        setUserInput(state => ({ ...state, total: formatNumber(total, {type: 'irt'}), amount: formatNumber(amount, { point: 6 }) }))
    }


    const onDepositClicked = () => {
        const coin = type === "buy" ? market.pair : market.coin
        window.location.href = HOME + `user/wallet-operations?coin=${coin}&type=${type}`
    }

    return (
        <CFlexCenter width={width > TABLET_SIZE ? "50%" : "100%"} direction={lang === "en" ? "ltr" : "rtl"}>
            <Padding padding={width > 480 ? "4px 20px" : "4px 5px"}>
                <FlexBetween height="fit-content">
                    {width > MOBILE_SIZE &&
                        <Flex
                            style={{ cursor: 'pointer' }}
                            onMouseEnter={() => setDepositHover(true)}
                            onMouseLeave={() => setDepositHover(false)}
                            onClick={onDepositClicked}
                        >
                            <TextWithMargin type={type} style={{ fontSize: '0.8rem', margin: '0' }}>
                                <Text tid={'deposit'} />
                            </TextWithMargin>
                            <DepositIcon size={22} type={type} hovered={depositHover} />
                        </Flex>
                    }
                    <DText>
                        {
                            formatNumber(
                                type === "buy" ? available.buy : available.sell, {point: 5}
                            )} {type === "buy" ? market.pair?.toUpperCase() : market.coin?.toUpperCase()}
                    </DText>
                </FlexBetween>
            </Padding>

            {activeTab !== 2 &&
                <FlexCenter>
                    <TradeInput
                        prefix="price"
                        id={'spot-price'}
                        suffix={market.pair}
                        value={userInput.price}
                        onValueChange={(v) => onValueChange(v, "price")}
                        isMarket={activeTab === 1}
                        error={ocoError.price}
                        type={type}
                    />
                </FlexCenter>
            }

            {(activeTab === 3 || activeTab === 2) &&
                <>
                    <Margin margin="6px 0" />
                    <FlexCenter>
                        <TradeInput
                            prefix="stop"
                            id={'spot-stop'}
                            suffix={market.pair}
                            value={userInput.stop}
                            onValueChange={(v) => onValueChange(v, "stop")}
                            error={ocoError.stop}
                            type={type}
                        />
                    </FlexCenter>
                </>
            }

            {(activeTab === 3 || activeTab === 2) &&
                <>
                    <Margin margin="6px 0" />
                    <FlexCenter>
                        <TradeInput
                            prefix="limit"
                            id={'spot-limit'}
                            suffix={market.pair}
                            value={userInput.limit}
                            onValueChange={(v) => onValueChange(v, "limit")}
                        />
                    </FlexCenter>
                </>
            }

            <>
                <Margin margin="6px 0" />
                <FlexCenter style={{position: "relative"}}>
                    <TradeInput
                        prefix="amount"
                        suffix={market.coin}
                        value={userInput.amount}
                        onValueChange={(v) => onValueChange(v, "amount")}
                        id={'spot-amount'}
                    />
                    <Tooltip right="40px" top="-45px" show={limitsError.error && userInput.amount}>
                        {limitsError.userError ?
                            <Text tid="not-enough-balance" />
                            :
                            <>
                                <Text tid={limitsError.type}/>
                                {`:${limitsError.value}`}
                            </>
                        }
                    </Tooltip>
                </FlexCenter>
            </>

            <Margin margin="6px 0" />
            <FlexCenter>
                <Padding padding={width > MOBILE_SIZE ? "0 20px" : "0 10px"}>
                    <Slider
                        val={sliderVal}
                        onSliderChange={onSliderChange}
                        disabled={available[type] === 0}
                    />
                </Padding>
            </FlexCenter>

            <Margin margin="18px 0" />
            {activeTab !== 1 &&
                <>
                    <FlexCenter>
                        <TradeInput
                            prefix="total"
                            suffix={market.pair}
                            value={userInput.total}
                            onValueChange={(v) => onValueChange(v, "total")}
                        />
                    </FlexCenter>
                </>
            }
            <Margin margin="8px 0" />
            <FlexCenter>
                <Padding padding="0 20px">
                    <TradeBtn
                        type={type}
                        verified={!!profile && !limitsError.error && valid}
                        warning={limitsError.userError}
                        onClick={
                            () => onOperationClicked(limitsError, limits)
                        }
                    >
                            <Text tid={
                                !!profile ? ( limitsError.userError ? "increase-balance" : type) : "signin"
                            } />
                    </TradeBtn>
                </Padding>
            </FlexCenter>
        </CFlexCenter>
    )
}


export default TradeType;
